import type { RateBasis } from '@chukua/common';
import { describeCargo, keyOf, laneOf, type RateLineKeyFields } from '../../../policies/rate-line-key';

/** The versions of one price: same lane, job type, cargo and basis, in the order the server returned them. */
export interface PriceGroup<L> {
  key: string;
  jobTypeCode: string;
  cargo: string;
  basis: RateBasis;
  lines: L[];
}

export interface LaneGroup<L> {
  lane: string;
  prices: PriceGroup<L>[];
}

/**
 * A lane is origin → destination, but a lane carries several prices (a job type, a cargo, a basis each), so a lane
 * splits further by the whole key — grouping on the lane alone would show two different prices as one line's history.
 * Lanes and prices keep the order they first appear in, so the server's sort still decides what comes first.
 */
export function groupLinesByLane<L extends RateLineKeyFields>(lines: L[]): LaneGroup<L>[] {
  const lanes = new Map<string, LaneGroup<L>>();
  const prices = new Map<string, PriceGroup<L>>();

  for (const line of lines) {
    const lane = laneOf(line);
    const group = lanes.get(lane) ?? { lane, prices: [] };
    lanes.set(lane, group);

    const key = keyOf(line);
    const price = prices.get(key) ?? { key, jobTypeCode: line.jobTypeCode, cargo: describeCargo(line), basis: line.basis, lines: [] as L[] };
    if (!prices.has(key)) group.prices.push(price);
    prices.set(key, price);
    price.lines.push(line);
  }

  return [...lanes.values()];
}
